import React, { useState, useEffect } from 'react';
import { X, User, Award, TrendingUp, AlertTriangle, MessageCircle, BookOpen, Trophy, Gift, Clock } from 'lucide-react';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const StudentProfileModal = ({ studentId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchProfile();
  }, [studentId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(`${BACKEND_URL}/api/mentor/student/${studentId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
    { id: 'quiz', label: 'Quizzes', icon: BookOpen },
    { id: 'activity', label: 'Activity', icon: Trophy },
    { id: 'followup', label: 'Follow-ups', icon: MessageCircle },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-4">
            {profile.profile_photo ? (
              <img src={profile.profile_photo} alt={profile.name} className="w-16 h-16 rounded-full border-4 border-white" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                <User className="w-8 h-8 text-blue-600" />
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold text-white">{profile.name}</h2>
              <p className="text-blue-100">{profile.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-blue-800 rounded-lg">
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="border-b bg-gray-50">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium border-b-2 ${
                    activeTab === tab.id ? 'border-blue-600 text-blue-600 bg-white' : 'border-transparent text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-green-600" />
                  Activation Status
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Status</p>
                    <p className="font-bold text-green-700">{profile.activation.activation_status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">First Login</p>
                    <p className="font-medium">{profile.activation.first_login ? new Date(profile.activation.first_login).toLocaleDateString() : 'Not yet'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">First Lesson</p>
                    <p className="font-medium">{profile.activation.first_lesson_completed ? new Date(profile.activation.first_lesson_completed).toLocaleDateString() : 'Not yet'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Days to Activate</p>
                    <p className="font-bold text-lg">{profile.activation.days_to_activate || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {profile.risk_flags.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Risk Flags ({profile.risk_flags.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.risk_flags.map((flag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                        {flag.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Weekly Minutes</p>
                  <p className="text-2xl font-bold text-blue-600">{Math.round(profile.weekly_learning_minutes)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Velocity</p>
                  <p className="text-2xl font-bold text-purple-600">{profile.velocity_days_per_lesson} d/l</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Quiz Attempts</p>
                  <p className="text-2xl font-bold text-orange-600">{profile.quiz_attempts.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">GP Balance</p>
                  <p className="text-2xl font-bold text-green-600">{profile.rewards.gp_balance}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border p-4 rounded-lg">
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                    Community Activity
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Posts:</span>
                      <span className="font-medium">{profile.community.posts_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Replies:</span>
                      <span className="font-medium">{profile.community.replies_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Activity Score:</span>
                      <span className="font-bold text-blue-600">{profile.community.activity_score}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border p-4 rounded-lg">
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-yellow-600" />
                    Challenges
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Attempted:</span>
                      <span className="font-medium">{profile.challenges.attempted_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Missed:</span>
                      <span className="text-red-600">{profile.challenges.missed_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">GP Earned:</span>
                      <span className="font-bold text-yellow-600">{profile.challenges.gp_earned}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Lesson Timeline ({profile.progress_timeline.length})</h3>
                {profile.stuck_on_lesson && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                    ⚠️ Stuck: {profile.stuck_on_lesson}
                  </span>
                )}
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {profile.progress_timeline.map((lesson, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${
                    lesson.completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex justify-between">
                      <div>
                        <h4 className="font-medium">{lesson.lesson_title}</h4>
                        <p className="text-sm text-gray-600">{lesson.module_title}</p>
                        <div className="flex gap-4 mt-2 text-xs">
                          <span className={lesson.completed ? 'text-green-600' : 'text-gray-500'}>
                            {lesson.completed ? '✓ Completed' : '⏳ In Progress'}
                          </span>
                          <span>Video: {lesson.video_watch_percentage}%</span>
                        </div>
                      </div>
                      {lesson.completion_date && (
                        <span className="text-xs text-gray-600">
                          {new Date(lesson.completion_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'quiz' && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg mb-4">Quiz History ({profile.quiz_attempts.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {profile.quiz_attempts.map((quiz, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${
                    quiz.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex justify-between">
                      <div>
                        <h4 className="font-medium">{quiz.quiz_title}</h4>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className={quiz.passed ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                            {quiz.score}%
                          </span>
                          <span>{quiz.passed ? '✓ Passed' : '✗ Failed'}</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-600">
                        {new Date(quiz.attempted_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-lg mb-3">Announcements ({profile.unseen_announcements_count} unseen)</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {profile.announcements.slice(0, 10).map((ann, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      ann.seen ? 'bg-gray-50' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex justify-between">
                        <span className="font-medium text-sm">{ann.title}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          ann.seen ? 'bg-gray-200' : 'bg-yellow-200'
                        }`}>
                          {ann.seen ? 'Seen' : 'Unseen'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {new Date(ann.sent_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-pink-600" />
                  Redeemed Rewards
                </h3>
                {profile.rewards.redeemed_rewards.length > 0 ? (
                  <div className="space-y-2">
                    {profile.rewards.redeemed_rewards.map((reward, idx) => (
                      <div key={idx} className="p-3 bg-pink-50 rounded-lg border">
                        <p className="font-medium text-sm">{reward.reward_name || 'Reward'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm">No rewards redeemed yet</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'followup' && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg mb-4">Follow-up History ({profile.followup_history.length})</h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {profile.followup_history.map((followup, idx) => (
                  <div key={idx} className="p-4 bg-white border rounded-lg">
                    <div className="flex justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-sm">{new Date(followup.call_timestamp).toLocaleDateString()}</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        followup.parent_sentiment === 'Positive' ? 'bg-green-100 text-green-800' :
                        followup.parent_sentiment === 'Negative' ? 'bg-red-100 text-red-800' : 'bg-gray-100'
                      }`}>
                        {followup.parent_sentiment}
                      </span>
                    </div>
                    <div className="bg-gray-50 p-3 rounded mb-2">
                      <p className="text-sm text-gray-600">{followup.mentor_notes}</p>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Commitment: <strong>{followup.student_commitment}</strong></span>
                      {followup.next_followup_date && (
                        <span className="text-blue-600">Next: {new Date(followup.next_followup_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
                {profile.followup_history.length === 0 && (
                  <p className="text-gray-600 text-center py-8">No follow-up history yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProfileModal;
